import { useCallback, useEffect, useRef, useState } from "react"
import type { Editor, NodeViewProps } from "@tiptap/react"
import { NodeViewWrapper, NodeViewContent } from "@tiptap/react"
import { NodeSelection } from "@tiptap/pm/state"

import { isValidPosition } from "../../../lib/tiptap-utils"

import "./image-node-view.scss"

export type EightHandlePosition =
  | "nw"
  | "ne"
  | "sw"
  | "se"
  | "n"
  | "s"
  | "w"
  | "e"

export interface ResizeSession {
  position: EightHandlePosition
  startX: number
  startY: number
  startWidth: number
  startHeight: number
  aspectRatio: number
}

// Only corner handles (keeps aspect ratio by design).
const HANDLE_POSITIONS: EightHandlePosition[] = ["nw", "ne", "sw", "se"]

function isSvgSrc(src: string | null | undefined): boolean {
  if (!src) return false
  return (
    src.toLowerCase().includes(".svg") || src.includes("image/svg+xml")
  )
}

function attrToStyleSize(
  attr: string | number | null | undefined
): string | undefined {
  if (attr == null || attr === "") return undefined
  const s = String(attr)
  if (s.includes("%")) return s
  if (s.includes("px")) return s
  const n = Number(s)
  return Number.isFinite(n) ? `${n}px` : s
}

export interface ResizableImageProps
  extends React.HTMLAttributes<HTMLDivElement> {
  src: string
  alt?: string
  editor?: Editor
  minSize?: number
  maxWidth?: number
  align?: "left" | "center" | "right"
  initialWidth?: string | number | null
  initialHeight?: string | number | null
  initialAspectRatio?: string | null
  showCaption?: boolean
  hasContent?: boolean
  indentDomProps?: Record<string, string>
  onImageResizeEnd?: (attrs: {
    width: string
    height: string
    aspectRatio?: string
  }) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdateAttributes?: (attrs: Record<string, any>) => void
  getPos: () => number | undefined
  nodeSize?: number
}

export function ImageNodeView(props: NodeViewProps) {
  const { editor, node, updateAttributes, getPos } = props
  const hasContent = node.content.size > 0
  const indentLevel = node.attrs.indent
  const indentDomProps =
    typeof indentLevel === "number" && indentLevel > 0
      ? { "data-ps-indent": String(indentLevel) }
      : {}

  return (
    <ResizableImage
      src={node.attrs.src}
      alt={node.attrs.alt || ""}
      editor={editor}
      align={node.attrs["data-align"]}
      initialWidth={node.attrs.width}
      initialHeight={node.attrs.height}
      initialAspectRatio={node.attrs.aspectRatio}
      showCaption={node.attrs.showCaption}
      hasContent={hasContent}
      indentDomProps={indentDomProps}
      nodeSize={node.nodeSize}
      onImageResizeEnd={({ width, aspectRatio }) =>
        updateAttributes({ width, height: null, aspectRatio: aspectRatio || null })
      }
      onUpdateAttributes={updateAttributes}
      getPos={getPos}
    />
  )
}

export const ResizableImage: React.FC<ResizableImageProps> = ({
  src,
  alt = "",
  editor,
  minSize = 50,
  maxWidth = 800,
  align = "left",
  initialWidth,
  initialHeight,
  initialAspectRatio,
  showCaption = false,
  hasContent = false,
  indentDomProps = {},
  nodeSize,
  onImageResizeEnd,
  onUpdateAttributes,
  getPos,
}) => {
  const [resizeSession, setResizeSession] = useState<
    ResizeSession | undefined
  >()
  /** Pixel size during an active resize — keeps layout stable on the render triggered by mousedown (see resizeSession). */
  const [liveDims, setLiveDims] = useState<{
    w: number
    h: number
  } | null>(null)
  const [showHandles, setShowHandles] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  // Caption visibility when selection leaves
  useEffect(() => {
    if (!editor || !showCaption) return

    const handleSelectionUpdate = () => {
      const pos = getPos()
      if (!isValidPosition(pos) || !nodeSize) return

      const { from, to } = editor.state.selection
      const nodeStart = pos
      const nodeEnd = pos + nodeSize

      const isOutsideNode = to < nodeStart || from > nodeEnd

      if (isOutsideNode && !hasContent && onUpdateAttributes) {
        onUpdateAttributes({ showCaption: false })
      }
    }

    editor.on("selectionUpdate", handleSelectionUpdate)
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    }
  }, [editor, showCaption, hasContent, getPos, nodeSize, onUpdateAttributes])

  const handleImageClick = useCallback(
    (event: React.MouseEvent) => {
      if (!editor || !getPos || resizeSession) return

      event.preventDefault()
      event.stopPropagation()

      const pos = getPos()
      if (isValidPosition(pos)) {
        editor.chain().focus().setNodeSelection(pos).run()
      }
    },
    [editor, getPos, resizeSession]
  )

  const effectiveMaxWidth = useCallback((): number => {
    const fromEditor =
      editor?.view.dom?.firstElementChild?.clientWidth ?? maxWidth
    let indentPx = 0
    const indented = contentRef.current?.closest("[data-ps-indent]")
    if (indented) {
      indentPx = parseFloat(getComputedStyle(indented).marginLeft) || 0
    }
    return Math.max(minSize, Math.min(fromEditor - indentPx, maxWidth))
  }, [editor, maxWidth, minSize, indentDomProps])

  const computeDimensions = useCallback(
    (session: ResizeSession, clientX: number, clientY: number) => {
      const { position, startX, startY, startWidth, startHeight, aspectRatio } =
        session
      const deltaX = clientX - startX
      const deltaY = clientY - startY

      let newWidth = startWidth
      let newHeight = startHeight

      // Corner resize: choose the dominant drag axis (x vs y) so resizing feels natural
      // while still enforcing the original aspect ratio.
      if (position.length === 2) {
        const useX = Math.abs(deltaX) >= Math.abs(deltaY)
        if (useX) {
          if (position.includes("e"))
            newWidth = Math.max(minSize, startWidth + deltaX)
          if (position.includes("w"))
            newWidth = Math.max(minSize, startWidth - deltaX)
          newHeight = newWidth / aspectRatio
        } else {
          if (position.includes("s"))
            newHeight = Math.max(minSize, startHeight + deltaY)
          if (position.includes("n"))
            newHeight = Math.max(minSize, startHeight - deltaY)
          newWidth = newHeight * aspectRatio
        }
      }

      // Clamp width for east/west and corner drags that affect width
      if (
        position.includes("e") ||
        position.includes("w") ||
        position.length === 2
      ) {
        const cap = effectiveMaxWidth()
        newWidth = Math.min(newWidth, cap)
        newHeight = newWidth / aspectRatio
      }

      return { newWidth, newHeight }
    },
    [effectiveMaxWidth, minSize]
  )

  const windowPointerMove = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!resizeSession || !editor) return

      const clientX =
        "touches" in event
          ? (event.touches[0]?.clientX ?? 0)
          : event.clientX
      const clientY =
        "touches" in event
          ? (event.touches[0]?.clientY ?? 0)
          : event.clientY

      const { newWidth, newHeight } = computeDimensions(
        resizeSession,
        clientX,
        clientY
      )
      setLiveDims({ w: newWidth, h: newHeight })
    },
    [computeDimensions, editor, resizeSession]
  )

  const endResize = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!editor) return

      if (!resizeSession) {
        const target =
          "touches" in event
            ? document.elementFromPoint(
                event.changedTouches[0]?.clientX ?? 0,
                event.changedTouches[0]?.clientY ?? 0
              )
            : event.target
        const insideContent =
          target && contentRef.current?.contains(target as Node)
        if ((!insideContent || !editor.isEditable) && showHandles) {
          setShowHandles(false)
        }
        return
      }

      const wasNodeSelection =
        editor.state.selection instanceof NodeSelection &&
        editor.state.selection.node.type.name === "image"

      const img = imageRef.current
      // Prefer layout box — matches what the user sees (also correct for click-without-drag).
      const finalWidth = img ? `${img.offsetWidth}px` : `${minSize}px`
      const finalHeight = img ? `${img.offsetHeight}px` : `${minSize}px`
      const aspectRatio =
        img && img.offsetWidth > 0 && img.offsetHeight > 0
          ? `${img.offsetWidth} / ${img.offsetHeight}`
          : undefined

      setResizeSession(undefined)
      setLiveDims(null)
      onImageResizeEnd?.({ width: finalWidth, height: finalHeight, aspectRatio })

      const pos = getPos()
      if (isValidPosition(pos) && wasNodeSelection) {
        editor.chain().focus().setNodeSelection(pos).run()
      }
    },
    [
      editor,
      getPos,
      minSize,
      onImageResizeEnd,
      resizeSession,
      showHandles,
    ]
  )

  const startResize = useCallback(
    (position: EightHandlePosition, clientX: number, clientY: number) => {
      const img = imageRef.current
      if (!img) return

      const startWidth = img.offsetWidth || minSize
      const startHeight = img.offsetHeight || minSize
      const aspectRatio =
        startHeight > 0 ? startWidth / startHeight : 1

      setLiveDims({ w: startWidth, h: startHeight })
      setResizeSession({
        position,
        startX: clientX,
        startY: clientY,
        startWidth,
        startHeight,
        aspectRatio,
      })
    },
    [minSize]
  )

  const onHandleMouseDown = (
    position: EightHandlePosition,
    e: React.MouseEvent
  ) => {
    e.preventDefault()
    e.stopPropagation()
    startResize(position, e.clientX, e.clientY)
  }

  const onHandleTouchStart = (
    position: EightHandlePosition,
    e: React.TouchEvent
  ) => {
    e.preventDefault()
    const t = e.touches[0]
    if (t) startResize(position, t.clientX, t.clientY)
  }

  useEffect(() => {
    window.addEventListener("mousemove", windowPointerMove)
    window.addEventListener("mouseup", endResize)
    window.addEventListener("touchmove", windowPointerMove, { passive: false })
    window.addEventListener("touchend", endResize)

    return () => {
      window.removeEventListener("mousemove", windowPointerMove)
      window.removeEventListener("mouseup", endResize)
      window.removeEventListener("touchmove", windowPointerMove)
      window.removeEventListener("touchend", endResize)
    }
  }, [endResize, windowPointerMove])

  // Sync attrs from doc → img when not resizing (avoid fighting React inline size while liveDims drives the img)
  useEffect(() => {
    if (resizeSession || liveDims) return
    const img = imageRef.current
    if (!img) return

    const w = attrToStyleSize(initialWidth)
    const h = attrToStyleSize(initialHeight)
    const ar = initialAspectRatio ? String(initialAspectRatio) : undefined

    if (w) img.style.width = w
    else img.style.removeProperty("width")

    if (ar) {
      ;(img.style as any).aspectRatio = ar
      img.style.height = "auto"
    } else {
      ;(img.style as any).aspectRatio = ""
      if (h) img.style.height = h
      else img.style.removeProperty("height")
    }

    if (isSvgSrc(src) && !w && !h) {
      img.style.width = "200px"
      img.style.height = "200px"
      img.style.objectFit = "contain"
    } else if (!isSvgSrc(src)) {
      img.style.objectFit = ""
    }
  }, [initialWidth, initialHeight, liveDims, resizeSession, src])

  const wrapperMouseEnterHandler = () => {
    if (editor?.isEditable) setShowHandles(true)
  }

  const wrapperMouseLeaveHandler = () => {
    if (resizeSession) return
    if (editor?.isEditable) setShowHandles(false)
  }

  const wrapperTouchStartHandler = () => {
    if (editor?.isEditable) setShowHandles(true)
  }

  const shouldShowCaption = showCaption || hasContent

  const widthStyle = attrToStyleSize(initialWidth)
  const heightStyle = attrToStyleSize(initialHeight)
  const aspectRatioStyle =
    initialAspectRatio != null && initialAspectRatio !== ""
      ? String(initialAspectRatio)
      : undefined

  return (
    <NodeViewWrapper
      onMouseEnter={wrapperMouseEnterHandler}
      onMouseLeave={wrapperMouseLeaveHandler}
      onTouchStart={wrapperTouchStartHandler}
      data-align={align}
      className="tiptap-image"
      {...indentDomProps}
    >
      <div className="tiptap-image-container">
        <div ref={contentRef} className="tiptap-image-content">
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            className="tiptap-image-img post-content-image-img"
            contentEditable={false}
            draggable={false}
            onClick={handleImageClick}
            style={{
              cursor: editor?.isEditable ? "pointer" : "default",
              width: liveDims ? `${liveDims.w}px` : widthStyle,
              ...(aspectRatioStyle ? { aspectRatio: aspectRatioStyle, height: "auto" } : {}),
              ...(liveDims
                ? { aspectRatio: `${liveDims.w} / ${liveDims.h}`, height: "auto" }
                : !aspectRatioStyle
                  ? { height: heightStyle }
                  : {}),
            }}
          />

          {showHandles &&
            editor?.isEditable &&
            HANDLE_POSITIONS.map((position) => (
              <div
                key={position}
                className={`resize-handle resize-${position}`}
                onMouseDown={(e) => onHandleMouseDown(position, e)}
                onTouchStart={(e) => onHandleTouchStart(position, e)}
              />
            ))}
        </div>

        {editor?.isEditable && shouldShowCaption && (
          <NodeViewContent
            as="figcaption"
            className="tiptap-image-caption image-caption-container ps-post-style-caption"
            data-placeholder="Add a caption..."
          />
        )}
      </div>
    </NodeViewWrapper>
  )
}
